import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  IconPersonVerified,
  Loader,
  useToast,
} from 'braid-design-system';
import { observer } from 'mobx-react';
import { type ComponentProps, useEffect, useState } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';

import { ChangeStatusButtons } from 'src/App/AppPageBlock/ProfileDrawer/Profile/ChangeStatusButtons';
import {
  type CheckReferencesActionOriginsProfileDrawer,
  type ProfileDrawerPageAnalytics,
  createPageAnalytics,
} from 'src/App/AppPageBlock/ProfileDrawer/createPageAnalytics';
import type { UseHandleStatusChangeResponse } from 'src/App/hooks/useHandleStatusChange';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { HOTJAR_VIEW_REFERENCE_CHECK_BUTTON_CLICKED } from 'src/constants/hotjar';
import { mapBucketsToAnalyticsApplicationStatus } from 'src/constants/pageTrackingData';
import { JOB_APPLICATION_TAB } from 'src/constants/tabs';
import { STATUS_CHANGE_LOCATION } from 'src/graphql/hirer/updateApplicationsStatus';
import { Display } from 'src/libs/Display';
import type { PageConfig } from 'src/libs/PageConfig';
import { UnlockReferenceCheckDialog } from 'src/libs/ReferenceChecks/UnlockReferenceCheckDialog';
import { CANDIDATE_PROFILE_DRAWER_TAB_PARAM_KEY } from 'src/pages/CandidateManagementPage/queryParams';
import { useDeepLinkedDetailView } from 'src/pages/CandidateManagementPage/utils/useDeepLinkedDetailView';
import type { IJobApplication } from 'src/stores/JobApplicationStore/JobApplication';
import type { Bucket } from 'src/stores/JobApplicationStore/types';
import type { IQuestionnaireStore } from 'src/stores/JobStore/QuestionnaireStore';
import { getReferenceBalance } from 'src/stores/JobStore/utils/getReferenceBalance';
import { convertToReferencesReceived } from 'src/utils/eventMappers';
import { useStores } from 'src/utils/hooks/useStores';
import { sendHotjarTag } from 'src/utils/hotjar';

import { OrderReferenceCheckDialog } from '../../OrderReferenceCheckDialog';
import {
  PurchaseReferenceCheckAddOnDialog,
  PurchaseReferenceCheckErrorDialog,
} from '../../PurchaseReferenceCheckAddOnDialog';
import {
  getJobApplicationReferenceCheckTrackingProps,
  getJobReferenceCheckTrackingProps,
} from '../../utils/getReferenceCheckTrackingProps';
import DetailsContentView from '../DetailsContentView';
import { getApplicationSeekReferenceStatus } from '../DetailsContentView/getApplicationSeekReferenceStatus';

import translations from './.vocab';
import ChangeBucketError from './ChangeBucketError';
import { getReferenceCheckCounts } from './getReferenceCheckCounts';

import * as styles from './JobApplicationDetails.css';

interface Props {
  jobApplication: IJobApplication;
  canPurchaseReferenceChecks: boolean;
  onUpdateStatusSuccess: () => Promise<void>;
  applicationCardIsExpanded?: boolean;
  showSuccessMessageToast: boolean;
  handleStatusChange: UseHandleStatusChangeResponse['handleStatusChange'];
  addCustomError: PageConfig['dataDogRum']['addCustomError'];
  userId: string;
  jobId: string;
  seekAdvertiserId: string;
  onMounted: () => void;
  referenceChecksBalance:
    | {
        expired: false;
        availableBalance: number;
        totalBalance: number;
        budgetType: 'LIMITED' | 'STANDARD';
      }
    | {
        expired: true;
        budgetType: 'LIMITED' | 'STANDARD';
      }
    | undefined;
}

export const JobApplicationDetails = observer(
  ({
    jobApplication: { isChangeStateError, isLoading },
    canPurchaseReferenceChecks,
    onUpdateStatusSuccess,
    jobApplication,
    showSuccessMessageToast,
    applicationCardIsExpanded,
    handleStatusChange,
    addCustomError,
    userId,
    jobId,
    seekAdvertiserId,
    onMounted,
    referenceChecksBalance,
  }: Props) => {
    const store = useStores();
    const questionnaireStore: IQuestionnaireStore =
      store.jobStore.questionnaire;
    const { jobApplicationStore, jobStore } = store;

    const { locale, environment, site } = useAppConfig();

    const { t } = useTranslations(translations);

    const { showDeepLinkedDetailView } = useDeepLinkedDetailView();

    const [referenceCheckDialog, setReferenceCheckDialog] = useState<
      | {
          type:
            | 'order-reference-check'
            | 'unlock-reference-check'
            | 'purchase-add-on-error'
            | null;
        }
      | {
          type: 'purchase-add-on';
          actionOrigin: CheckReferencesActionOriginsProfileDrawer;
        }
    >({ type: null });

    const onCloseReferenceCheckDialog = () =>
      setReferenceCheckDialog({ type: null });

    const [profileDrawerAnalytics] = useState<ProfileDrawerPageAnalytics>(() =>
      createPageAnalytics({
        site,
        locale,
        environment,
        userId: Number(userId),
        seekAdvertiserId,
      }),
    );

    const [, setTabQueryParam] = useQueryParam(
      CANDIDATE_PROFILE_DRAWER_TAB_PARAM_KEY,
      StringParam,
    );

    const {
      jobSeekReferenceStatus,
      orderReferenceCheckDialog,
      successReferenceCheckDialog,
      linkAction,
    } = getJobReferenceCheckTrackingProps(referenceChecksBalance);

    const { applicationSeekReferenceStatus } =
      getJobApplicationReferenceCheckTrackingProps(
        jobApplication.referenceCheckStatus,
      );

    const showToast = useToast();

    useEffect(() => {
      onMounted();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobApplication.applicationCorrelationId]);

    if (isLoading) {
      return <Loader />;
    }

    const hasValidBalance =
      referenceChecksBalance &&
      !referenceChecksBalance.expired &&
      referenceChecksBalance.availableBalance > 0;

    const getReferenceCheckProps = (): ComponentProps<
      typeof DetailsContentView
    >['referenceChecks'] => {
      if (!jobApplication.referenceCheckStatus) {
        return hasValidBalance || canPurchaseReferenceChecks
          ? {
              case: 'NOT_REQUESTED',
              showButton: true,
              onClick: () => {
                setReferenceCheckDialog(
                  hasValidBalance
                    ? { type: 'order-reference-check' }
                    : {
                        type: 'purchase-add-on',
                        actionOrigin:
                          'candidate profile reference check pressed',
                      },
                );
                profileDrawerAnalytics.trackLink({
                  event: 'reference_check_pressed',
                  attributes: {
                    isSeekReferenceJob,
                    seekReferenceBalance: referenceCreditBalance,
                    applicationCorrelationId,
                    bucket: mapBucketsToAnalyticsApplicationStatus(
                      jobApplication.bucket,
                    ),
                    candidateId: trackingCandidateId,
                    prospectId: trackingProspectId,
                    currentPage: 'candidate details',
                    jobSeekReferenceStatus,
                    seekListingId: trackingSeekListingId,
                  },
                });
              },
            }
          : {
              case: 'NOT_REQUESTED',
              showButton: false,
            };
      }

      const { referenceCheckStatus } = jobApplication;

      if (referenceCheckStatus.status === 'available') {
        const counts = {
          finishedCount: referenceCheckStatus.finishedCount,
          totalCount: referenceCheckStatus.totalCount,
        };
        if (referenceCheckStatus.referenceCheckType === 'LIMITED') {
          return hasValidBalance &&
            referenceChecksBalance.budgetType === 'STANDARD'
            ? {
                case: 'LIMITED_AVAILABLE',
                canUnlockStandardReferenceCheck: true,
                onClick: ({ actionOrigin }) => {
                  if (
                    actionOrigin === 'candidate profile reference check pressed'
                  ) {
                    profileDrawerAnalytics.trackLink({
                      event: 'upgrade_reference_pressed',
                      attributes: {
                        isSeekReferenceJob,
                        jobSeekReferenceStatus,
                        seekReferenceBalance: referenceCreditBalance,
                        bucket: mapBucketsToAnalyticsApplicationStatus(
                          jobApplication.bucket,
                        ),
                        currentPage: 'candidate details',
                        candidateId: trackingCandidateId,
                        prospectId: trackingProspectId,
                        applicationCorrelationId,
                        seekListingId: trackingSeekListingId,
                        dialogName:
                          'Candidate Management - Unlock Reference Report',
                      },
                    });
                  }

                  if (actionOrigin === 'limited reference check banner') {
                    profileDrawerAnalytics.trackLink({
                      event: 'banner_content_pressed',
                      attributes: {
                        bannerElement:
                          'Go to Unlock Reference Report Dialog Box',
                        displayedBanner: 'Unlock Standard Reference Banner',
                        seekListingId: trackingSeekListingId,
                        isSeekReferenceJob,
                        jobSeekReferenceStatus,
                        seekReferenceBalance: referenceCreditBalance,
                        bucket: mapBucketsToAnalyticsApplicationStatus(
                          jobApplication.bucket,
                        ),
                        currentPage: 'candidate details',
                        candidateId: trackingCandidateId,
                        prospectId: trackingProspectId,
                        applicationCorrelationId,
                      },
                    });
                  }

                  setReferenceCheckDialog({
                    type: 'unlock-reference-check',
                  });
                },
                counts,
              }
            : {
                case: 'LIMITED_AVAILABLE',
                canUnlockStandardReferenceCheck: false,
                onClick: ({ actionOrigin }) => {
                  if (
                    actionOrigin === 'candidate profile reference check pressed'
                  ) {
                    profileDrawerAnalytics.trackLink({
                      event: 'upgrade_reference_pressed',
                      attributes: {
                        isSeekReferenceJob,
                        jobSeekReferenceStatus,
                        seekReferenceBalance: referenceCreditBalance,
                        bucket: mapBucketsToAnalyticsApplicationStatus(
                          jobApplication.bucket,
                        ),
                        currentPage: 'candidate details',
                        candidateId: trackingCandidateId,
                        prospectId: trackingProspectId,
                        applicationCorrelationId,
                        seekListingId: trackingSeekListingId,
                        dialogName:
                          'Candidate Management - Unlock Full Reference Check Upgrade Now',
                      },
                    });
                  }

                  if (actionOrigin === 'limited reference check banner') {
                    profileDrawerAnalytics.trackLink({
                      event: 'banner_content_pressed',
                      attributes: {
                        bannerElement: 'Reference Check Add On Banner',
                        displayedBanner: 'Limited Reference Check Banner',
                        seekListingId: trackingSeekListingId,
                        isSeekReferenceJob,
                        jobSeekReferenceStatus,
                        seekReferenceBalance: referenceCreditBalance,
                        bucket: mapBucketsToAnalyticsApplicationStatus(
                          jobApplication.bucket,
                        ),
                        currentPage: 'candidate details',
                        candidateId: trackingCandidateId,
                        prospectId: trackingProspectId,
                        applicationCorrelationId,
                      },
                    });
                  }

                  setReferenceCheckDialog(
                    canPurchaseReferenceChecks
                      ? {
                          type: 'purchase-add-on',
                          actionOrigin,
                        }
                      : { type: 'purchase-add-on-error' },
                  );
                },
                counts,
              };
        }
        return {
          case: 'STANDARD_AVAILABLE',
          counts,
        };
      }

      return {
        case: 'REQUESTED',
        type: referenceCheckStatus.referenceCheckType,
      };
    };

    const referenceChecks = getReferenceCheckProps();

    const isSeekReferenceApplication = referenceChecks.case !== 'NOT_REQUESTED';
    const referenceCheckCounts = getReferenceCheckCounts(referenceChecks);
    const referenceCreditBalance = getReferenceBalance(referenceChecksBalance);

    const trackingSeekListingId = Number(jobId);
    const trackingCandidateId = Number(jobApplication.seekCandidateId);
    const trackingProspectId = jobApplication.id;
    const candidateFirstName = jobApplication.firstName;
    const candidateFullName = jobApplication.fullName;
    const applicationCorrelationId = jobApplication.applicationCorrelationId;
    const isSeekReferenceJob = Boolean(referenceChecksBalance);

    const onStatusChange = async (bucket: Bucket) => {
      const statusChangeLocation = STATUS_CHANGE_LOCATION.CANDIDATE_DETAIL;

      await handleStatusChange({
        store,
        jobApplicationStore,
        jobApplication,
        destinationBucket: bucket,
        statusChangeLocation,
        showToast,
        onUpdateSuccess: onUpdateStatusSuccess,
        isExpanded: applicationCardIsExpanded,
        showSuccessMessageToast,
        isSeekReferenceApplication,
        applicationSeekReferenceStatus:
          getApplicationSeekReferenceStatus(referenceChecks),
        referenceCheckCounts,
      });
    };

    const changeStatusButtons = (
      <ChangeStatusButtons
        jobApplication={{
          id: jobApplication.id,
          fullName: jobApplication.fullName,
          bucket: jobApplication.bucket,
        }}
        changeStatus={onStatusChange}
      />
    );

    return (
      <>
        {isChangeStateError && (
          <Box margin="xsmall">
            <ChangeBucketError />
          </Box>
        )}
        <Box display="flex" flexDirection="column" flexGrow={1}>
          <DetailsContentView
            jobApplication={jobApplication}
            canPurchaseReferenceChecks={canPurchaseReferenceChecks}
            locale={locale}
            questionnaireStore={questionnaireStore}
            jobId={jobId}
            addCustomError={addCustomError}
            changeStatusButtons={changeStatusButtons}
            userId={userId}
            onCloseVerificationsAlert={() => {
              profileDrawerAnalytics.trackLink({
                event: 'alert_dismiss_pressed',
                attributes: {
                  alertShownMessageCode:
                    'Candidate Management - Verified by SEEK Pass Banner',
                  seekListingId: trackingSeekListingId,
                  candidateId: trackingCandidateId,
                },
              });
            }}
            onProfileBodyVerificationsAlertMount={() => {
              profileDrawerAnalytics.trackView({
                event: 'candidate_management_banner_displayed',
                attributes: {
                  displayedBanner: 'Career History Verification Banner',
                  seekListingId: trackingSeekListingId,
                  candidateId: trackingCandidateId,
                },
              });
            }}
            onClickProfileBodyVerificationsTab={() => {
              setTabQueryParam(JOB_APPLICATION_TAB.VERIFICATIONS);
              profileDrawerAnalytics.trackLink({
                event: 'banner_content_pressed',
                attributes: {
                  bannerElement: 'Go to Verifications Tab',
                  displayedBanner: 'Career History Verification Banner',
                  seekListingId: trackingSeekListingId,
                  candidateId: trackingCandidateId,
                },
              });
            }}
            onClickVerificationsAlertLearnMore={() => {
              profileDrawerAnalytics.trackLink({
                event: 'banner_content_pressed',
                attributes: {
                  bannerElement: 'Go to Why Verify SEEK Pass Page',
                  displayedBanner:
                    'Candidate Management - Verified by SEEK Pass Banner',
                  seekListingId: trackingSeekListingId,
                  candidateId: trackingCandidateId,
                },
              });
            }}
            onClickVerificationsTabFooterLearnMore={() => {
              profileDrawerAnalytics.trackLink({
                event: 'learn_how_credential_are_verified_pressed',
                attributes: {
                  actionOrigin:
                    'Candidate Management Profile - Verification Tab',
                },
              });
            }}
            onClickProfileTabVerificationsLearnMore={() => {
              profileDrawerAnalytics.trackLink({
                event: 'learn_how_credential_are_verified_pressed',
                attributes: {
                  actionOrigin: 'Candidate Management Profile - Profile Tab',
                },
              });
            }}
            onClickReferencesTab={() => {
              profileDrawerAnalytics.trackLink({
                event: 'references_tab_pressed',
                attributes: {
                  isSeekReferenceApplication,
                  referencesReceived:
                    convertToReferencesReceived(referenceCheckCounts),
                  bucket: mapBucketsToAnalyticsApplicationStatus(
                    jobApplication.bucket,
                  ),
                  candidateId: trackingCandidateId,
                  prospectId: trackingProspectId,
                  applicationCorrelationId:
                    jobApplication.applicationCorrelationId,
                  seekListingId: trackingSeekListingId,
                  applicationSeekReferenceStatus,
                },
              });
              sendHotjarTag(HOTJAR_VIEW_REFERENCE_CHECK_BUTTON_CLICKED);
            }}
            onClickVerificationsTab={() => {
              profileDrawerAnalytics.trackLink({
                event: 'verifications_tab_pressed',
                attributes: {
                  seekListingId: trackingSeekListingId,
                  candidateId: trackingCandidateId,
                },
              });
            }}
            onClickCareerHistoryWhyThisCouldHappen={() => {
              profileDrawerAnalytics.trackLink({
                event: 'why_this_could_happen_pressed',
                attributes: {
                  actionOrigin:
                    'Candidate Management Profile - Verification Tab',
                },
              });
            }}
            referenceChecks={referenceChecks}
          />
        </Box>
        <Display mobile>
          <Box
            padding="small"
            textAlign="center"
            className={[styles.fixedToBottom, styles.topShadow]}
            display="flex"
            justifyContent="spaceBetween"
            alignItems="center"
            data={{ testid: 'change-status-buttons' }}
          >
            {referenceChecks.case === 'NOT_REQUESTED' &&
            referenceChecks.showButton ? (
              <Button
                aria-label={t('Check references')}
                variant="soft"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  referenceChecks.onClick();
                }}
              >
                <IconPersonVerified />
              </Button>
            ) : null}

            {referenceChecks.case === 'LIMITED_AVAILABLE' ? (
              <Button
                aria-label={t('Upgrade references')}
                variant="soft"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  referenceChecks.onClick({
                    actionOrigin: 'candidate profile reference check pressed',
                  });
                }}
              >
                <IconPersonVerified />
              </Button>
            ) : null}
            {changeStatusButtons}
          </Box>
        </Display>

        {hasValidBalance ? (
          <OrderReferenceCheckDialog
            open={referenceCheckDialog.type === 'order-reference-check'}
            onClose={onCloseReferenceCheckDialog}
            candidateDetails={{
              applicationCorrelationId,
              candidateFirstName,
              candidateFullName,
              candidateId: trackingCandidateId,
              currentPage: 'candidate details',
              prospectId: trackingProspectId,
            }}
            jobId={trackingSeekListingId}
            onCompleted={() => {
              if (referenceChecksBalance) {
                store.jobStore.job.jobInformation.deductReferenceCheckBalance();
                jobApplicationStore.jobApplications
                  .find(
                    (jobApp) =>
                      applicationCorrelationId ===
                      jobApp.applicationCorrelationId,
                  )
                  ?.setReferenceCheckStatusRequested(
                    referenceChecksBalance.budgetType,
                  );
              }
            }}
            referenceCreditBalance={referenceChecksBalance.availableBalance}
            budgetType={referenceChecksBalance.budgetType}
            trackOrderReferenceCheckDialogDisplayedEvent={() => {
              profileDrawerAnalytics.trackView({
                event: 'dialog_displayed',
                attributes: {
                  isSeekReferenceJob,
                  seekReferenceBalance: referenceCreditBalance,
                  bucket: mapBucketsToAnalyticsApplicationStatus(
                    jobApplication.bucket,
                  ),
                  currentPage: 'candidate details',
                  seekListingId: trackingSeekListingId,
                  candidateId: trackingCandidateId,
                  prospectId: trackingProspectId,
                  applicationCorrelationId,
                  dialogName: orderReferenceCheckDialog,
                  jobSeekReferenceStatus,
                },
              });
            }}
            trackOrderReferenceCheckDialogDismissedEvent={() => {
              profileDrawerAnalytics.trackLink({
                event: 'dialog_dismiss_pressed',
                attributes: {
                  isSeekReferenceJob,
                  seekReferenceBalance: referenceCreditBalance,
                  bucket: mapBucketsToAnalyticsApplicationStatus(
                    jobApplication.bucket,
                  ),
                  currentPage: 'candidate details',
                  seekListingId: trackingSeekListingId,
                  candidateId: trackingCandidateId,
                  prospectId: trackingProspectId,
                  applicationCorrelationId,
                  dialogName: orderReferenceCheckDialog,
                  jobSeekReferenceStatus,
                },
              });
            }}
            trackOrderReferenceCheckPressedEvent={() => {
              profileDrawerAnalytics.trackLink({
                event: 'order_reference_check_pressed',
                attributes: {
                  isSeekReferenceJob,
                  seekReferenceBalance: referenceCreditBalance,
                  bucket: mapBucketsToAnalyticsApplicationStatus(
                    jobApplication.bucket,
                  ),
                  currentPage: 'candidate details',
                  seekListingId: trackingSeekListingId,
                  candidateId: trackingCandidateId,
                  prospectId: trackingProspectId,
                  applicationCorrelationId,
                  jobSeekReferenceStatus,
                  linkAction,
                  linkText: 'Order reference check',
                  dialogName: orderReferenceCheckDialog,
                },
              });
            }}
            trackOrderReferenceCheckErrorDialogDisplayedEvent={() => {
              profileDrawerAnalytics.trackView({
                event: 'dialog_displayed',
                attributes: {
                  seekListingId: trackingSeekListingId,
                  dialogName:
                    "Candidate Management - SEEKRef - Couldn't check references",
                  currentPage: 'candidate details',
                },
              });
            }}
            trackOrderReferenceCheckErrorDialogDismissedEvent={() => {
              profileDrawerAnalytics.trackLink({
                event: 'dialog_dismiss_pressed',
                attributes: {
                  seekListingId: trackingSeekListingId,
                  dialogName:
                    "Candidate Management - SEEKRef - Couldn't check references",
                  currentPage: 'candidate details',
                },
              });
            }}
            trackOrderReferenceCheckSuccessDialogDisplayedEvent={() => {
              profileDrawerAnalytics.trackView({
                event: 'dialog_displayed',
                attributes: {
                  isSeekReferenceJob,
                  seekReferenceBalance: referenceCreditBalance,
                  bucket: mapBucketsToAnalyticsApplicationStatus(
                    jobApplication.bucket,
                  ),
                  currentPage: 'candidate details',
                  seekListingId: trackingSeekListingId,
                  candidateId: trackingCandidateId,
                  prospectId: trackingProspectId,
                  applicationCorrelationId,
                  dialogName: successReferenceCheckDialog,
                  jobSeekReferenceStatus,
                },
              });
            }}
            trackOrderReferenceCheckSuccessDialogDismissedEvent={() => {
              profileDrawerAnalytics.trackLink({
                event: 'dialog_dismiss_pressed',
                attributes: {
                  isSeekReferenceJob,
                  seekReferenceBalance: referenceCreditBalance,
                  bucket: mapBucketsToAnalyticsApplicationStatus(
                    jobApplication.bucket,
                  ),
                  currentPage: 'candidate details',
                  seekListingId: trackingSeekListingId,
                  candidateId: trackingCandidateId,
                  prospectId: trackingProspectId,
                  applicationCorrelationId,
                  dialogName: successReferenceCheckDialog,
                  jobSeekReferenceStatus,
                },
              });
            }}
          />
        ) : null}

        <PurchaseReferenceCheckErrorDialog
          open={referenceCheckDialog.type === 'purchase-add-on-error'}
          onClose={onCloseReferenceCheckDialog}
        />

        {hasValidBalance ? (
          <UnlockReferenceCheckDialog
            open={referenceCheckDialog.type === 'unlock-reference-check'}
            onClose={() => {
              profileDrawerAnalytics.trackLink({
                event: 'dialog_dismiss_pressed',
                attributes: {
                  isSeekReferenceJob,
                  seekReferenceBalance: referenceCreditBalance,
                  bucket: mapBucketsToAnalyticsApplicationStatus(
                    jobApplication.bucket,
                  ),
                  currentPage: 'candidate details',
                  seekListingId: trackingSeekListingId,
                  candidateId: trackingCandidateId,
                  prospectId: trackingProspectId,
                  applicationCorrelationId,
                  dialogName: 'Candidate Management - Unlock Reference Report',
                  jobSeekReferenceStatus,
                },
              });
              onCloseReferenceCheckDialog();
            }}
            jobId={jobId}
            goToReport={() => {
              showDeepLinkedDetailView({
                jobApplicationId: jobApplication.id,
                tab: JOB_APPLICATION_TAB.REFERENCES,
              });
            }}
            candidateDetails={{
              candidateFirstName: jobApplication.firstName,
              applicationCorrelationId: jobApplication.applicationCorrelationId,
            }}
            onCompleted={() => {
              jobStore.job.jobInformation.deductReferenceCheckBalance();
              jobApplication.unlockReferenceCheck();
            }}
            remainingCount={referenceChecksBalance.availableBalance - 1}
            onClickUpgradeReferenceButton={() => {
              profileDrawerAnalytics.trackLink({
                event: 'order_reference_check_pressed',
                attributes: {
                  isSeekReferenceJob: true,
                  jobSeekReferenceStatus,
                  seekReferenceBalance: referenceChecksBalance.availableBalance,
                  bucket: mapBucketsToAnalyticsApplicationStatus(
                    jobApplication.bucket,
                  ),
                  applicationCorrelationId:
                    jobApplication.applicationCorrelationId,
                  prospectId: trackingProspectId,
                  candidateId: trackingCandidateId,
                  currentPage: 'candidate details',
                  dialogName: 'Candidate Management - Unlock Reference Report',
                  linkAction: 'Unlock Reference Check',
                  linkText: 'unlock reference',
                  seekListingId: trackingSeekListingId,
                },
              });
            }}
            onMount={() => {
              profileDrawerAnalytics.trackView({
                event: 'dialog_displayed',
                attributes: {
                  isSeekReferenceJob,
                  seekReferenceBalance: referenceCreditBalance,
                  bucket: mapBucketsToAnalyticsApplicationStatus(
                    jobApplication.bucket,
                  ),
                  currentPage: 'candidate details',
                  seekListingId: trackingSeekListingId,
                  candidateId: trackingCandidateId,
                  prospectId: trackingProspectId,
                  applicationCorrelationId,
                  dialogName: 'Candidate Management - Unlock Reference Report',
                  jobSeekReferenceStatus,
                },
              });
            }}
          />
        ) : null}

        {referenceCheckDialog.type === 'purchase-add-on' ? (
          <PurchaseReferenceCheckAddOnDialog
            actionOrigin={referenceCheckDialog.actionOrigin}
            jobId={trackingSeekListingId}
            onClickPurchase={() => {
              profileDrawerAnalytics.trackLink({
                event: 'purchase_reference_check_add_on_pressed',
                attributes: {
                  actionOrigin: referenceCheckDialog.actionOrigin,
                  isSeekReferenceJob,
                  jobSeekReferenceStatus,
                  seekReferenceBalance: referenceCreditBalance,
                  bucket: mapBucketsToAnalyticsApplicationStatus(
                    jobApplicationStore.bucket,
                  ),
                  currentPage: 'candidate details',
                  seekListingId: trackingSeekListingId,
                  candidateId: trackingCandidateId,
                  prospectId: trackingProspectId,
                  applicationCorrelationId,
                  linkAction: 'Go To Purchase Reference Check Add On Modal',
                  linkText: 'purchase add on',
                  dialogName:
                    'Candidate Management - Unlock Full Reference Check Upgrade Now',
                },
              });
            }}
            onClose={() => {
              profileDrawerAnalytics.trackLink({
                event: 'dialog_dismiss_pressed',
                attributes: {
                  isSeekReferenceJob,
                  seekReferenceBalance: referenceCreditBalance,
                  bucket: mapBucketsToAnalyticsApplicationStatus(
                    jobApplication.bucket,
                  ),
                  currentPage: 'candidate details',
                  seekListingId: trackingSeekListingId,
                  candidateId: trackingCandidateId,
                  prospectId: trackingProspectId,
                  applicationCorrelationId,
                  dialogName:
                    'Candidate Management - Unlock Full Reference Check Upgrade Now',
                  jobSeekReferenceStatus,
                },
              });
              onCloseReferenceCheckDialog();
            }}
            onMount={() =>
              profileDrawerAnalytics.trackView({
                event: 'dialog_displayed',
                attributes: {
                  actionOrigin: referenceCheckDialog.actionOrigin,
                  applicationCorrelationId,
                  bucket: mapBucketsToAnalyticsApplicationStatus(
                    jobApplicationStore.bucket, // TODO: double check if hirer current status view or application status
                  ),
                  candidateId: trackingCandidateId,
                  currentPage: 'candidate details',
                  isSeekReferenceJob,
                  jobSeekReferenceStatus,
                  prospectId: trackingProspectId,
                  seekListingId: trackingSeekListingId,
                  seekReferenceBalance: referenceCreditBalance,
                  dialogName:
                    'Candidate Management - Unlock Full Reference Check Upgrade Now',
                },
              })
            }
            open
          />
        ) : null}
      </>
    );
  },
);
